<template>
  <div class="datepickerController">
    <div
      class="datepickerInput__container" :class="['--has-fancy', {'--disabled': dates === null }, {'--double': isDouble }]"
    >
      <div class="datepickerInput">
        <label v-if="showInputLabel"
               class="datepickerInput__label"
               for="departure_date"
        >
          {{ $t(!isRoundTrip || isDouble ? 'departure_date' : 'date') }}*
        </label>
        <div class="datepickerInput__group">
          <div class="datepickerInput__icon">
            <i class="fas fa-calendar-day"></i>
          </div>
          <date-picker
            v-model="departure"
            :range="isRange"
            :key="keyComponent"
            :ref="`datepickerDeparture_${id}`"
            type="date"
            :placeholder="!isRoundTrip || isDouble ? $t('placeholder_departure_date') : $t('placeholder_date')"
            :lang="$store.getters.getLanguage"
            v-validate="validators"
            :data-vv-as="$t('departure_date')"
            :name="`departure_date_${id}`"
            :id="`departure_date_${id}`"
            :disabled-date='isDisableDate'
            :title-format="'DD/MM/YYYY'"
            :format="'DD/MM/YYYY'"
            value-type="YYYY-MM-DD"
            :editable="false"
            :disabled="dates === null"
            :default-value="defaultDepartureDate"
            @change="changeDeparture"
            @pick="setStartDate"
            @open="resetStartDate"
            @input="notify"
            @start-date="setStartDate"
            @close="closeDatepicker"
            @update:open="datePickerOpen()"
            @calendar-change="loadFares"
            @panel-change="panelChange" 
            :popup-style="isFullScreenMobile ? { top: 0, bottom: 0, left: 0, right: 0 } : {}"
          >
            <div slot="icon-calendar"></div>
          </date-picker>
        </div>
        <div v-if="errors.first(`departure_date_${id}`)" class="datepickerInput__feedback">{{ errors.first(`departure_date_${id}`) }}</div>
      </div>
      <div class="datepickerInput" v-if="isDouble">
        <label
          v-if="showInputLabel"
          class="datepickerInput__label"
          for="arrival_date"
        >
          {{ $t('return_date') }}*
        </label>
        <div class="datepickerInput__group">
          <div class="datepickerInput__icon">
            <i class="fas fa-calendar-week"></i>
          </div>

          <date-picker
            type="date"
            v-validate="validators"
            v-model="arrival"
            value-type="YYYY-MM-DD"
            :range="isRange"
            :ref="`datepickerArrival_${id}`"
            :default-value="defaultArrivalDate"
            :placeholder='$t("placeholder_return_date")'
            :lang="$store.getters.getLanguage"
            :data-vv-as="$t('arrival_date')"
            :name="`arrival_date_${id}`"
            :id="`arrival_date_${id}`"
            :disabled-date='isLowerThanDeparture'
            :title-format="'DD/MM/YYYY'"
            :format="'DD/MM/YYYY'"
            :editable="false"
            :disabled="dates === null || !this.departure"
            @input="notify"
            @open="$store.dispatch('notifyOverflowToParent', true)"
            @close="$store.dispatch('notifyOverflowToParent', false)"
            @update:open="datePickerOpen('secondDatePicker')"
            @calendar-change="loadFares"
            @panel-change="panelChange" 
            :popup-style="isFullScreenMobile ? { top: 0, bottom: 0, left: 0, right: 0 } : {}"
          >
            <div slot="icon-calendar"></div>
          </date-picker>
        </div>
        <div v-if="errors.first(`arrival_date_${id}`)" class="datepickerInput__feedback">{{ errors.first(`arrival_date_${id}`) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/es'
import 'vue2-datepicker/locale/it'
import 'vue2-datepicker/locale/fr'
import 'vue2-datepicker/locale/pt'
import 'vue2-datepicker/locale/de'

export default {
  name: 'DatePickerController',
  inject: [
    '$validator'
  ],
  props: {
    id: {
      required: false,
      default: ''
    },
    isRoundTrip: {
      required: true
    },
    isDoubleDatepicker: {
      required: true
    },
    dates: {
      required: true
    },
    initialValue: {
      required: false,
      default: () => ['', '']
    },
    showInputLabel: {
      required: false
    },
    openDatePicker: {
      required: false
    },
    keepDatepickerClearIcon: {
      required: false,
      default: false,
      type: Boolean
    },
    datepickerSuccessColor: {
      required: false,
      default: false,
      type: Boolean
    },
    validators: {
      default: () => ({
        required: true
      }),
      required: false,
      type: Object
    },
    showFares: {
      type: Boolean | String,
      default: false
    },
    fares: {
      type: Object,
      default: () => ({}),
      required: false
    },
    currency: {
      type: String,
      required: false,
      default: ''
    }
  },
  watch: {
    dates: {
      handler (newVal) {
        if (!newVal) {
          this.departure = ''
          this.arrival = ''
        }
      }
    }
  },
  components: {
    DatePicker
  },
  data () {
    return {
      departure: '',
      arrival: '',
      startDate: undefined,
    }
  },
  created() {
    this.departure = this.setDepartureByComponentType()
    this.arrival = this.initialValue ? this.initialValue[1] : ''
  },
  mounted () {
    if (this.keepDatepickerClearIcon) {
      let el = this.$el.querySelector(`.datepickerInput__group`)
      el.classList.add('keeped-icon')
    }
  },
  methods: {
    panelChange(data) {
      if (!this.showFares) return
      this.$nextTick(() => {
        if (this.isFullScreenMobile) {
          const mxCalendar = document.querySelectorAll('.mx-calendar')
          mxCalendar.forEach(e => e.style.width = '100%')
        }
        
        if (data === 'date') {
          this.loadCalendarAttrs()
          this.loadCurrency()
        }
      })
    },
    loadCalendarAttrs() {
      const calendars = document.querySelectorAll('.mx-calendar.mx-calendar-panel-date')
      if (!calendars.length) { return }
      calendars.forEach((calendar, i) => {
        calendar.querySelector('.mx-calendar-header').setAttribute('name', 'datePickerHeader')
        calendar.querySelector('.mx-calendar-content').setAttribute('name', 'datePickerContent')
        calendar.id = i === 0 ? 'firstDatePicker' : 'secondDatePicker'
        calendar.classList.add('yearNavHide')
        if (this.showFares) {
          calendar.classList.add('calendar-prices')
          if (this.isFullScreenMobile && this.isRange && this.isRoundTrip) calendar.classList.add('range')
        }
      })
    },
    loadFares() {
      if (!this.showFares) return
      this.$nextTick(() => {
        document.querySelectorAll('td.cell').forEach((td) => {
          const date = td.getAttribute('title')
          const _price = td.querySelector('.additional-content')

          if (_price) _price.remove()
          if (td.classList.contains('disabled')) return
        
          if (this.fares[date]) {
            td.appendChild(this.getFare(date))
          }
        })
      })
    },
    setDepartureByComponentType: function () {
      if (this.isRoundTrip) {
        return this.isDoubleDatepicker && this.initialValue ? this.initialValue[0] : this.initialValue
      }
      return this.initialValue ? this.initialValue : ''
    },
    notify: function () {
      this.$emit('input', this.isDouble ? [this.departure, this.arrival] : this.departure)
    },
    setStartDate: function (date) {
      this.startDate = date
    },
    changeDeparture: function () {
      this.arrival = ''
      if (this.isDouble && !this.isFullScreenMobile) {
        this.$nextTick(() => this.$refs[`datepickerArrival_${this.id}`].focus())
      }
      if (this.datepickerSuccessColor) {
        let el = this.$el.querySelector(`.datepickerInput__group`)
        if (this.departure) {
          el.classList.add('datepicker-color--success')
        } else {
          el.classList.remove('datepicker-color--success')
        }
      }
      this.notify()
    },
    isDisableDate: function (date) {
      let dateFormatted = moment(date).format('YYYY-MM-DD')
      return !this.availableDates.includes(dateFormatted)
    },
    isLowerThanDeparture: function (date) {
      const returnAvailableDates = this.dates.rt.filter((d) => {
        return d >= moment(this.departure).format('YYYY-MM-DD')
      })
      return moment(date).diff(moment(this.departure)) < 0 ||
        !returnAvailableDates.includes(moment(date).format('YYYY-MM-DD'))
    },
    getFare(date) {
      const fareContainer = document.createElement('div')
      fareContainer.style.color = this.getFareColor(this.fares[date])
      const fare = this.fares[date].toLocaleString('en-US', {
        maximumFractionDigits: 1,
        notation: 'compact',
        compactDisplay: 'short'
      })
      fareContainer.textContent = fare
      fareContainer.classList.add('additional-content')
    
      return fareContainer
    },
    getFareColor (fare) {
      if (fare > this.getMaxAvg) {
        return '#CF364C'
      } else if (this.getMaxAvg > fare && fare > this.getMinAvg) {
        return '#D97706'
      } else if (fare < this.getMinAvg) {
        return '#43946C'
      }
    },
    addElementOnce(parent, element) {
      if (parent.contains(element)) {
        element.remove()
      }
      parent.appendChild(element)
    },
    loadCurrency () {
      const currencySpan = document.createElement('span')
      currencySpan.className = 'currency'
      currencySpan.innerHTML = `<i class="fa fa-dollar"></i> ${this.currency || ''}`

      if (!this.isFullScreenMobile) {
        const calendarPrices = document.querySelector('.calendar-prices')
        const currencyContainer = document.createElement('div')
        const currencyContainerToRemove = document.querySelector('.currencyContainer')
        if (currencyContainerToRemove) currencyContainerToRemove.remove()
        const returnCalendar = document.querySelector('#secondDatePicker')
        currencyContainer.className = 'currencyContainer'
        this.addElementOnce(currencyContainer, currencySpan)
        this.addElementOnce(calendarPrices, currencyContainer)
        if (returnCalendar) this.addElementOnce(returnCalendar, currencyContainer)
      }
      
      return currencySpan
    },
    datePickerOpen (datepicker) {
      this.$nextTick(() => {
        this.loadCalendarAttrs()

        if (this.showFares) {
          const datepickerContent = document.querySelector('.mx-datepicker-content')
          datepickerContent.classList.add('--fares')
          document.querySelectorAll('td.cell').forEach((td) => {
            const date = td.getAttribute('title')
            if (td.querySelector('.additional-content') || td.classList.contains('disabled')) return
            if (this.fares[date]) {
              td.appendChild(this.getFare(date))
            }
          })

          const currencySpan = this.loadCurrency()
        
          if (this.isFullScreenMobile) {
            let wrapper = null
            const mxCalendar = document.querySelectorAll('.mx-calendar')
            mxCalendar.forEach(e => e.style.width = '100%')
            const container = document.createElement('div')
            container.style.display = 'flex'
            container.style.alignItems = 'center'
            container.style.gap = '1rem'
            container.style.fontSize = '1rem'
            container.style.padding = '6px 12px'
            currencySpan.style.marginLeft = 'auto'

            const firstIcon = document.createElement('i')
            const span = document.createElement('span')
            const closeButton = document.createElement('button')
            closeButton.style.background = 'transparent'
            closeButton.style.border = 'none'
            const closeIcon = document.createElement('i')
            closeIcon.classList.add('fas', 'fa-times')
            closeButton.appendChild(closeIcon)

            if (this.isRange) {
              wrapper = document.getElementsByClassName('mx-range-wrapper')[0]
              firstIcon.classList.add('fas', 'fa-plane-departure')
              const textContent = this.$t('date')
              span.textContent = textContent
              closeButton.onclick = () => {
                this.$refs[`datepickerDeparture_${this.id}`].closePopup()
              }
            } else {
              wrapper = document.getElementsByClassName('mx-datepicker-body')[0]
              const secondDatePicker = datepicker === 'secondDatePicker'
              firstIcon.classList.add('fas', secondDatePicker ? 'fa-plane-arrival' : 'fa-plane-departure')
              const textContent = this.$t(secondDatePicker ? 'placeholder_return_date' : 'placeholder_departure_date')
              span.textContent = textContent
              
              closeButton.onclick = () => {
                if (datepicker === 'secondDatePicker') {
                  this.$refs[`datepickerArrival_${this.id}`].closePopup()
                } else {
                  this.$refs[`datepickerDeparture_${this.id}`].closePopup()
                }
              }
            }

            container.appendChild(firstIcon)
            container.appendChild(span)
            container.appendChild(currencySpan)
            container.appendChild(closeButton)
            wrapper.prepend(container)
          }
        }
      })
    },
    resetStartDate () {
      this.startDate = null
      this.$store.dispatch('notifyOverflowToParent', true)
    },
    closeDatepicker: function () {
      this.startDate = null
      this.$store.dispatch('notifyOverflowToParent', false)
    },
  },
  watch: {
    openDatePicker: function (value) {
      if (value) {
        this.$nextTick(() => {
          if (!this.initialValue)
            this.$refs[`datepickerDeparture_${this.id}`]?.focus()
            this.$emit('changeAutoOpenDatepicker', false)
        })
      }
    },
    dates: function (value) {
      if (!value) {
        this.departure = null
        this.arrival = null
      }
    },
    isRoundTrip: function () {
      this.departure = this.setDepartureByComponentType()
    }
  },
  computed: {
    isFullScreenMobile () {
      return this.$breakpoint.isMobile && this.showFares
    },
    isSimple: function () {
      return !this.isRoundTrip
    },
    isRange: function () {
      return this.isRoundTrip && !this.isDoubleDatepicker
    },
    isDouble: function () {
      return this.isRoundTrip && this.isDoubleDatepicker
    },
    keyComponent: function () {
      return `${this.isSimple}-${this.isRange}-${this.isDouble}`
    },
    selectingArrAtDouble: function () {
      return ((this.departure && !this.arrival) && this.isDouble)
    },
    availableDates: function () {
      let result = this.dates.ow
      if (this.startDate || this.selectingArrAtDouble) {
        if (this.startDate) {
          result = this.dates.rt
          result = this.dates.rt.filter((d) => {
            return d >= moment(this.startDate).format('YYYY-MM-DD')
          })
        }
      }
      return result
    },
    defaultDepartureDate: function () {
      if (this.departure && typeof(this.departure) === 'object' &&
        this.departure.filter(x=> x != null).length > 0) {
        return null
      }
      if (this.dates){
        return this.availableDates.length ?  new Date(this.availableDates[0]) : new Date()
      }
      return new Date()
    },
    defaultArrivalDate: function () {
      if (this.dates){
        if (this.departure){
          const departure = new Date(this.departure);
          const arrival = new Date(this.availableDates[0]);
          if (departure > arrival){
            return departure
          }
          return arrival
        }
        return this.availableDates.length ?  new Date(this.availableDates[0]) : new Date()
      }
      return new Date()
    },
    getMinFare() {
      if (!this.showFares) return 0
      return Math.min(...Object.values(this.fares))
    },
    getMaxFare() {
      if (!this.showFares) return 0
      return Math.max(...Object.values(this.fares))
    },
    getAvgFare() {
      return (this.getMinFare + this.getMaxFare) / 2
    },
    getMaxAvg() {
      return (this.getMaxFare + this.getAvgFare) / 2
    },
    getMinAvg() {
      return (this.getMinFare + this.getAvgFare) / 2
    },
  }
}
</script>

<style lang="sass">
.datepickerController
  display: flex
  width: 100%
  .datepickerInput
    width: 100%
    &__container
      width: 100%
      display: flex
      &.--double
        flex-wrap: wrap
        position: relative
        justify-content: space-between
        .datepickerInput
          width: calc(50% - .25rem)
          .mx-datepicker
            width: 100%
          .mx-input
            margin: 0
      .fancyProgress
        bottom: 1rem

.yearNavHide
  .mx-btn-icon-double-left,
  .mx-btn-icon-double-right
    display: none!important
/* I added the 'yearNavHide' class, in case product people
  later want it to be configurable by carrier, so we play
  with this class, according to the configuration. (;p) */
.calendar-prices
  .mx-calendar-content
    height: 400px
    .mx-table
      border-spacing: 7px
  .mx-table-date td
    height: 58px
  &.range
    .mx-table-date td
      height: 32px

@media (min-width: 768px)
  .calendar-prices
    &.mx-calendar
      width: 375px
    .mx-table-date td
      height: 40px

body
  .mx-datepicker
    width: 100%
  .mx-datepicker-main
    background-color: var(--color-surface)
    color: var(--color-onSurface)
    border: 1px solid var(--color-overlay-20)
    z-index: 100000
    .mx-datepicker-content
      background: #fff
    .mx-table-date
      .cell
        &.today
          color: var(--color-primary)
          border-radius: var(--b-radius-sm)
        &.disabled
          background-color: var(--color-overlay-20)
          color: var(--color-onSurface)
          opacity: .5
        &.active
          border-radius: var(--b-radius-sm)
          color: var(--color-onSecondary)
          background-color: var(--color-secondary)
        &.in-range
          color: var(--color-onSecondary)
          background-color: var(--color-secondary)
          position: relative
          opacity: .7
        &:hover:not(.disabled)
          color: var(--color-onSecondary)
          background-color: var(--color-secondary)
    .mx-calendar+.mx-calendar
      border-left: 1px solid var(--color-overlay-30)
    .mx-btn
      color: var(--color-primary)
// @media (min-width: 992px)
//   body
//     .datepickerController
//       .datepickerInput
//         &__container
//           &.--double
//             .mx-datepicker
//               max-width: 160px
.datepicker-color--success
  color: var(--color-success)
  .mx-input-wrapper
    .mx-input
      color: var(--color-success) !important
.keeped-icon
  .mx-icon-clear
    display: block
.currency
  background-color: var(--color-overlay-10)
  border-radius: 6px
  padding: .25rem .5rem
.currencyContainer
  display: flex
  margin: .5rem 0
  justify-content: flex-end
// estilos del datepicker con los fares activados
.mx-datepicker-content.--fares
  .cell
    border-radius: 6px
</style>
